import type { ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

export type SelectionCardProps = {
  title: string;
  description: string;
  selected?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

/**
 * Selectable option card matching the TCI Calculator Figma selection tiles
 * (node 362:330 — sharp corners, cyan L-brackets, translucent glass fill).
 */
export function SelectionCard({
  title,
  description,
  selected = false,
  className,
  type = "button",
  ...props
}: SelectionCardProps) {
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={cn(
        "flex h-[123px] w-full min-w-0 flex-1 flex-col justify-between border border-[rgba(56,189,248,0.35)] text-left backdrop-blur-[8px] transition-colors duration-200",
        "hover:border-[#38bdf8] hover:bg-[rgba(56,189,248,0.22)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
        selected
          ? "bg-[rgba(56,189,248,0.35)] hover:bg-[rgba(56,189,248,0.45)]"
          : "bg-transparent",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className="size-4 shrink-0 border-l-2 border-t-2 border-[#38bdf8]"
      />

      <span className="flex w-full flex-col gap-[9px] px-5">
        <span className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
          {title}
        </span>
        <span className="font-['Inter'] text-sm font-normal leading-[22px] tracking-[-0.7px] text-[rgba(255,255,255,0.8)]">
          {description}
        </span>
      </span>

      <span className="flex w-full items-center justify-end">
        <span
          aria-hidden
          className="size-4 shrink-0 border-b-2 border-r-2 border-[#38bdf8]"
        />
      </span>
    </button>
  );
}
